import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const NAV_ITEMS = [
  { id: 'about', label: 'About' },
  { id: 'compliance', label: 'Compliances' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'products', label: 'Products' },
  { id: 'government', label: 'Clients' },
  { id: 'manufacturing', label: 'Infrastructure' },
  { id: 'materials', label: 'Materials', path: '/materials' },
  { id: 'sustainability', label: 'Sustainability', path: '/sustainability' },
  { id: 'people', label: 'People', path: '/people' },
  { id: 'investor-relations', label: 'Investor Relations', path: '/investor-relations' },
  { id: 'contact', label: 'Contact' },
];

const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [scrollProgress, setScrollProgress] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const scrollItems = NAV_ITEMS.filter(item => !item.path);
    const handleScroll = () => {
      // Scroll progress
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);

      // Active section tracking (homepage only)
      if (!isHomePage) return;
      const sections = scrollItems.map(item => document.getElementById(item.id));
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i] && sections[i].getBoundingClientRect().top <= 200) {
          setActiveSection(scrollItems[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  const handleNavClick = (item) => {
    setMobileOpen(false);

    if (item.path) {
      navigate(item.path);
      window.scrollTo(0, 0);
      return;
    }

    if (isHomePage) {
      const el = document.getElementById(item.id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: item.id } });
    }
  };

  const isActive = (item) => {
    if (item.path) return location.pathname === item.path;
    return isHomePage && activeSection === item.id;
  };

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div
        data-testid="scroll-progress"
        className="fixed top-0 left-0 z-[60] h-[3px]"
        style={{
          width: `${scrollProgress}%`,
          background: '#C8102E',
          transition: 'width 0.1s linear',
        }}
      />

      <motion.nav
        data-testid="main-navigation"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          background: 'rgba(28, 28, 30, 0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          height: '60px',
        }}
        className="fixed top-0 left-0 right-0 z-50"
      >
        <div className="container-main flex items-center justify-between h-full">
          <button onClick={() => { navigate('/'); window.scrollTo(0, 0); }} className="flex items-center gap-2" data-testid="nav-logo">
            <span className="font-display text-[0.78rem] font-bold tracking-[0.3em] uppercase text-white">
              QUALIANCE
            </span>
          </button>

          <div className="hidden lg:flex items-center">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                data-testid={`nav-${item.id}`}
                className="px-1.5 py-2 font-mono text-[0.54rem] font-medium tracking-[0.06em] uppercase transition-colors duration-300 whitespace-nowrap"
                style={{
                  color: isActive(item) ? '#FFFFFF' : 'rgba(255, 255, 255, 0.55)',
                  borderBottom: isActive(item) ? '2px solid #C8102E' : '2px solid transparent',
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => handleNavClick({ id: 'contact' })}
              data-testid="nav-cta"
              className="flex items-center gap-1.5 px-3 py-1.5 lg:px-4 lg:py-2 text-white text-[0.5rem] lg:text-[0.58rem] font-mono font-medium uppercase tracking-[0.08em] rounded transition-colors whitespace-nowrap"
              style={{ background: '#C8102E' }}
            >
              Get in Touch <ChevronRight size={10} className="hidden sm:block" />
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-white"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 pt-[60px]"
            style={{ background: 'rgba(28, 28, 30, 0.98)', backdropFilter: 'blur(24px)' }}
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center gap-1 py-8">
              {NAV_ITEMS.map((item, i) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => handleNavClick(item)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="w-full text-center py-4 font-display text-lg tracking-wide"
                  style={{ color: isActive(item) ? '#C8102E' : 'rgba(255, 255, 255, 0.7)' }}
                >
                  {item.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
